import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  chain,
  get,
  groupBy,
  identity,
  isNumber,
  orderBy,
  startCase,
  sumBy,
} from 'lodash';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { ActionButton } from '@bbkAdminComponents/buttons/action-button';
import FormSelect from '@bbkAdminComponents/form-select/form-select';
import {
  PivotTableContext,
  PivotTableContextProvider,
  type PivotTableContextProps,
} from '@bbkAdminComponents/pivot-table/pivot-table-context';
import { PivotTableFilter } from '@bbkAdminComponents/pivot-table/pivot-table-filter';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { cs } from '@bbkAdminUtils/css-utils';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { Icons } from '@bbkAdminUtils/icons';
import {
  type ElementType,
  type ExtractReturns,
  type Prettify,
} from '@bbkAdminUtils/utility-types';
import { ExportCSV } from '@bbkShared/utils/export-file';

import './bbk-pivottable.scss';

const Collapse: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M10.0117 18.6036L14.8947 12.9782C14.9001 12.9718 14.908 12.97 14.9127 12.9637C15.1424 12.6991 15.2569 12.3486 15.2561 11.999C15.2569 11.6495 15.1432 11.2999 14.9127 11.0343C14.9072 11.028 14.9001 11.0262 14.8947 11.0199L10.0117 5.39631C9.55299 4.8679 8.80268 4.8679 8.34401 5.39631C7.88533 5.92472 7.88533 6.78912 8.34401 7.31753L12.4085 12L8.34401 16.6825C7.88533 17.2109 7.88533 18.0753 8.34401 18.6037C8.80268 19.1321 9.55378 19.1321 10.0117 18.6037V18.6036Z" />
  </svg>
);

export namespace PivotT {
  export type DataType = Record<string, unknown>;

  export type FnAny = (...args: any[]) => any;

  export type PivotTableGroupObject<T, K extends keyof T = keyof T> = {
    key: K;
    value: T[K];
  };
  export type WithInternalPivotFields<T, K extends keyof T = keyof T> = T & {
    __PIVOT_TABLE: {
      GROUP_KEY: string;
      GROUP_OBJECT: PivotTableGroupObject<T, K>[];
    };
  };

  export type CalculatedFields<T extends DataType> = {
    (row: T): Record<string, () => unknown>;
  };

  export type CalculatedFieldsLoose<T extends DataType> = {
    (row: any): Record<string, () => any>;
  };

  export type ComputedFieldsOnly<
    T extends DataType,
    CF extends CalculatedFields<T>,
  > = Prettify<ExtractReturns<ReturnType<CF>>>;

  export type ComputedFields<
    T extends DataType,
    CF extends CalculatedFields<T>,
  > = Prettify<T & ComputedFieldsOnly<T, CF>>;

  export type PivotValue<T extends DataType> = {
    field: keyof T;
    label: string;
    format: (val: number) => string;
    tooltip?: (row: T) => React.ReactNode;
  };

  export type GroupField<T extends DataType> = {
    field: Array<keyof T>;
    label: string;
    name: string;
    icon: React.ReactNode;
    tooltip?: (row: any) => React.ReactNode;
  };

  export type PivotValueLoose<T extends DataType> = {
    field: string;
    label: string;
    format: (val: number) => string;
    tooltip?: (row: any) => React.ReactNode;
  };

  export type GroupFieldLoose<T extends DataType> = {
    field: Array<string>;
    label: string;
    name: string;
    icon: React.ReactNode;
    tooltip?: (row: any) => React.ReactNode;
  };
}

class PivotTable<
  T extends PivotT.DataType,
  CF extends PivotT.CalculatedFields<T> = PivotT.CalculatedFields<T>,
  PR extends PivotT.ComputedFields<T, CF> = PivotT.ComputedFields<T, CF>,
> {
  public static GROUP_KEY_SEPARATOR = '-[|||]-';

  private calculatedFields: CF;

  constructor(calculatedFields: CF) {
    this.calculatedFields = calculatedFields;
  }

  public calculateRow = <TT extends T>(
    row: TT
  ): PivotT.ComputedFields<TT, CF> => {
    const newFields = Object.fromEntries(
      Object.entries(this.calculatedFields(row)).map(([key, fn]) => {
        const val = fn();
        return [key, Number.isNaN(val) ? 0 : val];
      })
    ) as PivotT.ComputedFieldsOnly<TT, CF>;
    return {
      ...row,
      ...newFields,
    };
  };

  public getGroupKey = (x: PR, fields: string[]): string =>
    fields.map((field) => get(x, field)).join(PivotTable.GROUP_KEY_SEPARATOR);

  private getGroupObject = (
    x: PR,
    fields: string[]
  ): PivotT.PivotTableGroupObject<PR>[] =>
    fields.map((field) => ({ key: field, value: get(x, field) }));

  private rollup = (values: PR[]): PR => {
    const firstRow = values[0];
    const keys = Object.keys(firstRow ?? []);
    return Object.fromEntries(
      keys.map((k) => [
        k,
        isNumber(get(firstRow, k)) ? sumBy(values, k) : get(firstRow, k),
      ])
    ) as PR;
  };

  public group = (
    values: PR[],
    fields: string[] = []
  ): PivotT.WithInternalPivotFields<PR>[] => {
    const grouped = groupBy(values, (x) => this.getGroupKey(x, fields));
    return Object.entries(grouped)
      .map(([GROUP_KEY, arr]) => ({
        ...this.rollup(arr),
        __PIVOT_TABLE: {
          GROUP_KEY,
          GROUP_OBJECT: this.getGroupObject(arr[0]!, fields),
        },
      }))
      .map((el) => this.calculateRow(el));
  };
}

type PivotTableProps<
  T extends PivotT.DataType,
  CF extends PivotT.CalculatedFieldsLoose<T> = PivotT.CalculatedFieldsLoose<T>,
  PR extends PivotT.ComputedFields<T, CF> = PivotT.ComputedFields<T, CF>,
> = {
  data: T[];
  calculatedFields: CF;
  pivotValues: PivotT.PivotValueLoose<PR>[];
  visibleColumns: string[];
  onVisibleColumnsChange?: (value: string[]) => void;
  tableName?: string;
  showTotal?: boolean;
  pivotRows: PivotT.GroupFieldLoose<PR>[];
  isFetching?: boolean;
  onClick?: (el: PivotT.WithInternalPivotFields<PR>) => void;
  selected?: PivotT.WithInternalPivotFields<PR>;
  isExportDisabled?: boolean;
  exportTooltip?: {
    content: React.ReactNode;
    tooltipOptions: { placement: string };
  };
};

const PivotTableEl = (props: PivotTableProps<PivotT.DataType>) => {
  const {
    data,
    pivotValues,
    visibleColumns: initVisibleColumns,
    onVisibleColumnsChange,
    tableName,
    showTotal = true,
    calculatedFields,
    pivotRows: initPivotRows,
    isFetching,
    onClick,
    selected,
    isExportDisabled = false,
    exportTooltip,
  } = props;
  type T = ElementType<typeof data>;
  type CF = typeof calculatedFields;
  type PR = PivotT.ComputedFields<T, CF>;
  type PF = keyof PR;

  const { groupByOpts } = useContext(PivotTableContext);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [sortField, setSortField] = useState<PF>(pivotValues[0]!.field);
  const [sortOrderDesc, setSortOrderDesc] = useState(true);

  const [visibleColumns, setVisibleColumnsState] =
    useState<PF[]>(initVisibleColumns);

  const setVisibleColumns: typeof setVisibleColumnsState = (args) => {
    const value = typeof args === 'function' ? args(visibleColumns) : args;
    onVisibleColumnsChange?.(value);
    setVisibleColumnsState(args);
  };

  useEffect(() => {
    setVisibleColumns(initVisibleColumns);
  }, [initVisibleColumns]);

  const visiblePivotValues = pivotValues.filter((pv) =>
    visibleColumns.includes(pv.field)
  );

  const pivotRows = groupByOpts
    .map((gbo) => initPivotRows.find((pr) => pr.name === gbo))
    .filter(isDefined);

  const allGroupFields = pivotRows.flatMap((x) => x.field);

  const pivotTable = useMemo(
    () => new PivotTable(calculatedFields),
    [calculatedFields]
  );

  const group = (values: PR[], fields: PF[] = []) => {
    const result = pivotTable.group(values, fields);

    return orderBy(
      result,
      [(row) => row[sortField]],
      [sortOrderDesc ? 'desc' : 'asc']
    );
  };

  const renderRow = (
    row: PivotT.WithInternalPivotFields<PR>,
    level = 0,
    type: PF | 'total' = 'total',
    renderExpand = false
  ) => {
    const configRow = pivotRows.find((pr) => pr.field.includes(type));
    const groupKey = row.__PIVOT_TABLE.GROUP_KEY;
    const pivotRowObj = pivotRows[level];
    const rowTooltip = pivotRowObj?.tooltip?.(row);
    const isSelected =
      selected?.__PIVOT_TABLE.GROUP_KEY === row.__PIVOT_TABLE.GROUP_KEY;
    return (
      <tr
        className={cs(`bbk-pivottable-${type}`, {
          'bbk-pivottable-row-level-1': level === 1,
          'bbk-pivottable-row-level-2': level === 2,
          'bbk-pivottable-row-level-3': level === 3,
          'bbk-pivottable-row-level-4': level === 4,
          'bbk-pivottable-row-level-5': level === 5,
          'bbk-pivottable-row-selected': isSelected,
        })}
        key={type}
      >
        <td className="bbk-pivottable-group">
          <div className="bbk-pivottable-group-container tw-items-center tw-truncate">
            <div className="tw-flex tw-flex-row">
              {renderExpand && (
                <span
                  onClick={() =>
                    setExpanded({
                      ...expanded,
                      [groupKey]: !expanded[groupKey],
                    })
                  }
                  className={cs(
                    'tw-mr-1 tw-inline-block tw-cursor-pointer tw-transition-transform',
                    {
                      'tw-rotate-90': expanded[groupKey],
                      'tw-text-cbrprimary-600': !isSelected,
                    }
                  )}
                >
                  <Collapse />
                </span>
              )}
              <span className="tw-mr-1">{configRow?.icon}</span>
              <span
                className="tw-cursor-pointer"
                onClick={() => onClick?.(row)}
              >
                {rowTooltip ? (
                  <TippyTooltip content={rowTooltip}>
                    <span>
                      {groupKey.split(PivotTable.GROUP_KEY_SEPARATOR).pop()}
                    </span>
                  </TippyTooltip>
                ) : (
                  <>{groupKey.split(PivotTable.GROUP_KEY_SEPARATOR).pop()}</>
                )}
              </span>
            </div>
          </div>
        </td>
        {visiblePivotValues.map(({ tooltip, format, field }) => {
          const value = (format ?? identity)(row[field] as number);
          return (
            <td className="bbk-pivottable-numbers" key={field}>
              {tooltip ? (
                <TippyTooltip content={tooltip(row)}>
                  <span>{value}</span>
                </TippyTooltip>
              ) : (
                value
              )}
            </td>
          );
        })}
      </tr>
    );
  };
  const renderGroup = (
    rawData: PR[],
    groupFields: string[],
    lvl: number,
    fldIdx: number
  ) => (
    <>
      {group(rawData, groupFields).map((groupped) => {
        let nextField: string | undefined;
        let hasKidsData = false;
        let j = fldIdx;
        const subData = rawData.filter((x) =>
          groupFields.every((gf) => x[gf] === groupped[gf])
        );
        do {
          nextField = allGroupFields[++j];
          hasKidsData = nextField
            ? subData.some((x) => isDefined(x[nextField!]))
            : false;
        } while (j < allGroupFields.length && !hasKidsData);
        const groupKey = pivotTable.getGroupKey(groupped, groupFields);
        const row = renderRow(groupped, lvl, groupFields.at(-1), hasKidsData);

        let rowKids: React.ReactNode | undefined;
        if (hasKidsData && expanded[groupKey]) {
          rowKids = renderGroup(
            subData,
            [...groupFields, nextField!],
            lvl + 1,
            j
          );
        }
        return (
          <Fragment key={groupKey}>
            {row}
            {rowKids}
          </Fragment>
        );
      })}
    </>
  );
  const renderBody = () => {
    const flatData = data.map((el) => pivotTable.calculateRow(el));
    return (
      <>
        {flatData.length === 0 && (
          <tr>
            <td colSpan={visiblePivotValues.length + 1}>No data available.</td>
          </tr>
        )}
        {allGroupFields[0] && renderGroup(flatData, [allGroupFields[0]], 0, 0)}
        {showTotal && group(flatData).map((x) => renderRow(x))}
      </>
    );
  };
  const renderToggleColumns = () => (
    <FormSelect
      className="tw-flex tw-justify-end tw-pr-1.5"
      popoverClassName="bbk-opr-popover-toggle-column"
      label={<span className="tw-pl-[5px]">Toggle column</span>}
      placeholder="..."
      toggleItem={(item) => {
        if (item === null) return;
        if (item.name === 'all') {
          if (visibleColumns.length === pivotValues.length) {
            setVisibleColumns([]);
          } else {
            setVisibleColumns(pivotValues.map((pv) => pv.field));
          }
          return;
        }
        const el = item as { id: string };
        if (visibleColumns.includes(el.id)) {
          const toRemove = visibleColumns.indexOf(el.id);
          setVisibleColumns([
            ...visibleColumns.slice(0, toRemove),
            ...visibleColumns.slice(toRemove + 1),
          ]);
          return;
        }
        setVisibleColumns([...visibleColumns, el.id]);
      }}
      allItemsSelected={visibleColumns.length === pivotValues.length}
      multiple
      items={pivotValues.map((pv) => ({
        selected: visibleColumns.includes(pv.field),
        id: pv.field,
        name: pv.label,
      }))}
      disabled={false}
    />
  );

  const exportData = () => {
    try {
      const fields = [
        ...pivotRows.flatMap((pr) => pr.field),
        ...visibleColumns,
      ].map((c) => {
        const f = pivotValues.find((pv) => pv.field === c);
        if (!f) {
          GlobalLogger.warn(`Could not find column ${c} in `, pivotValues);
          return { value: c, label: startCase(c) };
        }
        return { value: f.field, label: f.label };
      });

      const dataGrouped = chain(
        group(
          data.map((el) => pivotTable.calculateRow(el)),
          pivotRows.flatMap((x) => x.field)
        )
      )
        .map((x) =>
          Object.fromEntries(
            Object.entries(x).map(([k, v]) => {
              const pivotV = pivotValues.find((y) => y.field === k);
              const formattedValue = (pivotV?.format ?? identity)(v as number);

              return [k, formattedValue];
            })
          )
        )
        .orderBy(
          [...pivotRows.flatMap((x) => x.field), sortField],
          [...pivotRows.map(() => 'asc' as 'asc' | 'desc'), 'desc']
        )
        .value();

      console.log(dataGrouped);

      ExportCSV(dataGrouped, tableName ?? 'Table', fields);
    } catch (e) {
      const err = e as string;
      GlobalLogger.error(`Export from pivot table failed: ${err}`);
      BlackToastTrigger({
        content: 'Something went wrong. Please refresh and try again',
      });
    }
  };

  return (
    <>
      <div className="tw-flex tw-justify-end">
        <ActionButton
          title="Export"
          classes="brightPurple tw-mr-2"
          iconClass="brightPurple"
          onClick={exportData}
          disabled={isExportDisabled || isFetching || data.length === 0}
          tooltip={exportTooltip}
        />
        {renderToggleColumns()}
      </div>
      <div className="tw-overflow-x-scroll">
        <table className="bbk-pivottable tw-min-w-full">
          <thead className="bbk-pivottable-header">
            <tr>
              <th scope="col" className="tw-w-1/4">
                <PivotTableFilter
                  opts={[
                    {
                      value: undefined,
                      label: 'None',
                    },
                    ...initPivotRows.map((ipr) => ({
                      value: ipr.name,
                      label: ipr.label,
                      icon: ipr.icon,
                    })),
                  ]}
                />
              </th>
              {visiblePivotValues.map((v) => (
                <th
                  scope="col"
                  className="bbk-pivottable-numbers tw-cursor-pointer"
                  key={v.field}
                  onClick={() => {
                    if (sortField === v.field) {
                      setSortOrderDesc(!sortOrderDesc);
                    } else {
                      setSortField(v.field);
                      setSortOrderDesc(true);
                    }
                  }}
                >
                  <div className="tw-flex tw-select-none tw-justify-end">
                    {v.label}
                    <span
                      className={cs('tw-ml-1 tw-self-center', {
                        'tw-invisible': sortField !== v.field,
                        'tw-rotate-180': !sortOrderDesc,
                      })}
                    >
                      {Icons.chevronDown}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bbk-pivottable-body">
            {isFetching ? (
              <tr>
                <td colSpan={visiblePivotValues.length + 1}>
                  <span className="loading">{Icons.loaderSW6}</span> Loading...
                </td>
              </tr>
            ) : (
              renderBody()
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export const PivotTableComponent: React.FC<
  React.ComponentProps<typeof PivotTableEl> & PivotTableContextProps
> = ({ groupByOpts, onGroupByOptsChange, ...pivotTableProps }) => (
  <PivotTableContextProvider
    groupByOpts={groupByOpts}
    onGroupByOptsChange={onGroupByOptsChange}
  >
    <PivotTableEl {...pivotTableProps} />
  </PivotTableContextProvider>
);
